/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink, useNavigate } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";
import { COMPANY_NAME } from "constants/constant";
import { Collapse, IconButton } from "@mui/material";

// ['Dashboard','Admins','Judges','Groups','Users','Payment History','Categories','Abstract Categories','Papers','Event Info]
const adminRoutes = ['Judges', 'Groups', 'Speakers', 'Paper', 'Speaker Abstract', 'Poster', 'Accompanying Persons']
export const mobileAdminRoutes = ['Mobile Users', 'Exhibitors', 'Committee', 'Events', 'Reports', 'Downloads', 'Quick', 'Quiz', 'Quiz Results', 'Days', 'Abstract Doc', 'Scanner', 'Attendence']
export const hotelAdminRoutes = ['Bookings']

const judgeRoutes = ['Assigned Paper', "Assigned Poster"]
function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const navigation = useNavigate()

  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");

  let textColor = "white";
  const [openDropdown, setOpenDropdown] = useState({});

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    window.addEventListener("resize", handleMiniSidenav);
    handleMiniSidenav();

    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  const toggleDropdown = (key) => {
    setOpenDropdown((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  const adminType = localStorage.getItem('type')
  let filterRoute = routes

  if (adminType !== "superadmin") {
    if (adminType === "admin") {
      filterRoute = routes?.filter(i => adminRoutes?.includes(i?.name))
    }
    if (adminType === "judge") {
      filterRoute = routes?.filter(i => judgeRoutes?.includes(i?.name))
    }
    if (adminType === 'mobileadmin') {
      filterRoute = routes?.filter(i => mobileAdminRoutes?.includes(i?.name))
    }
    if (adminType === 'hoteladmin') {
      filterRoute = routes?.filter(i => hotelAdminRoutes?.includes(i?.name))
    }
  } else {
    filterRoute = routes?.filter(i => i?.name !== "Assigned Paper" && i?.name !== "Assigned Poster")

  }
  const renderRoutes = filterRoute?.filter(({ key }) => !['roles', 'staffs', 'website', 'charges', 'feedbacks', 'profile', 'notifications'].includes(key)).map(({ type, name, icon, title, noCollapse, key, href, route, items }) => {
    if (type === "dropdown") {
      return (
        <React.Fragment key={key}>
          <SidenavCollapse
            name={name}
            icon={icon}
            type={'dropdown'}
            active={collapseName.startsWith(key)}
            onClick={() => toggleDropdown(key)}
          />
          <Collapse in={openDropdown[key]} timeout="auto" unmountOnExit style={{ paddingLeft: 20 }}>
            {items.map((subItem) => (
              <NavLink key={subItem.key} to={subItem.route} >
                <SidenavCollapse name={subItem.name} icon={subItem.icon} active={subItem.key === collapseName} />
              </NavLink>
            ))}
          </Collapse>
        </React.Fragment>
      );
    }

    if (type === "collapse") {
      return href ? (
        <Link href={href} key={key} target="_blank" rel="noreferrer" sx={{ textDecoration: "none", }}>
          <SidenavCollapse name={name} icon={icon} active={key === collapseName} noCollapse={noCollapse} />
        </Link>
      ) : (
        <NavLink key={key} to={route}>
          <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
        </NavLink>
      );
    }

    if (type === "title") {
      return (
        <MDTypography key={key} color={textColor} display="block" variant="caption" fontWeight="bold" textTransform="uppercase" pl={3} mt={2} mb={1} ml={1}>
          {title}
        </MDTypography>
      );
    }

    if (type === "divider") {
      return (
        <Divider key={key} light={(!darkMode && !whiteSidenav && !transparentSidenav) || (darkMode && !transparentSidenav && whiteSidenav)} />
      );
    }

    return null;
  });

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}>
      <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox display={{ xs: "block", xl: "none" }} position="absolute" top={0} right={0} p={1.625} onClick={closeSidenav} sx={{ cursor: "pointer" }}>
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <MDBox component="img" src={'/logo.png'} alt="Brand" width="2rem" />}
          <MDBox width={!brandName && "100%"} sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}>
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {COMPANY_NAME}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
      <Divider light={(!darkMode && !whiteSidenav && !transparentSidenav) || (darkMode && !transparentSidenav && whiteSidenav)} />
      <List>{renderRoutes}</List>
      <MDBox p={2} mt="auto">
        {/* <MDButton component="a" href="/authentication/sign-in" rel="noreferrer" variant="gradient" color={'error'} fullWidth>
          Log out
        </MDButton> */}
        <IconButton
          style={{ width: "100%" }}
          onClick={() => {
            localStorage.removeItem('loginTime');
            localStorage.removeItem('sessionExpirationTime');
            navigation(`/authentication/sign-in`)
          }}
          size="small" disableRipple>
          {/* <Icon style={{ color: "red" }}>logout</Icon> */}

          <MDButton component="a" href="/authentication/sign-in" rel="noreferrer" variant="gradient" color={'error'} fullWidth>
            Log out</MDButton>

        </IconButton>
      </MDBox>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
